import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListWebhooksResult, type WebhookDeliveriesResult } from '@whinerp/client';
import { formatCount, formatDateTime } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Setup > Developer > Webhooks (F-093): endpoints and their delivery log with
 * payload, every attempt and replay - "the delivery log with replay is the
 * feature" - and inbound sources with their recent events.
 */

type Endpoint = ListWebhooksResult['endpoints'][number];
const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

export function WebhooksPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-093');
  const { can } = useSession();
  const allowed = enabled.enabled && can('setup.modules.manage');
  const data = useQuery<ListWebhooksResult, ApiError>({ queryKey: ['webhooks'], queryFn: () => api.listWebhooks({}), enabled: allowed });
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [secret, setSecret] = useState<string | null>(null);
  const [sourceName, setSourceName] = useState('');
  const [flowId, setFlowId] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['webhooks'] });
  const create = useMutation({
    mutationFn: () => api.saveWebhook({ body: { endpointId: null, name, url, events, active: true } }),
    onSuccess: async (result) => {
      setSecret(result.secret);
      setName('');
      setUrl('');
      setEvents([]);
      await refresh();
    },
  });
  const createSource = useMutation({
    mutationFn: () => api.createInboundSource({ body: { name: sourceName, flowId } }),
    onSuccess: async (result) => {
      setToken(result.token);
      setSourceName('');
      await refresh();
    },
  });

  if (!allowed) return null;
  const apiBase = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '/api';

  return (
    <Panel data-region="webhooks">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('webhooks.title')}</SectionLabel>
        {data.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : data.isError ? (
          <p className="text-dense text-danger-text">{errorText(data.error)}</p>
        ) : (
          <>
            <ul className="flex flex-col">
              {data.data.endpoints.map((endpoint) => (
                <li key={endpoint.endpointId} className="border-t border-t-rule py-2 first:border-t-0">
                  <EndpointRow endpoint={endpoint} />
                </li>
              ))}
            </ul>
            {secret ? (
              <p data-webhook-secret className="rounded-control bg-warning-tint px-3 py-2 text-dense text-ink">
                {t('webhooks.secretOnce')} <code className="break-all">{secret}</code>
              </p>
            ) : null}
            <div data-webhook-new className="flex flex-col gap-2 rounded-card border border-rule p-2">
              <SectionLabel>{t('webhooks.new')}</SectionLabel>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input data-webhook-name aria-label={t('webhooks.name')} placeholder={t('webhooks.name')} className={INPUT} value={name} onChange={(e) => setName(e.target.value)} />
                <input data-webhook-url aria-label={t('webhooks.url')} placeholder="https://" className={INPUT} value={url} onChange={(e) => setUrl(e.target.value)} />
              </div>
              <div className="flex flex-wrap gap-2" role="group" aria-label={t('webhooks.events')}>
                {data.data.events.map((event) => (
                  <label key={event} className="flex items-center gap-1 text-meta text-ink">
                    <input
                      type="checkbox"
                      data-webhook-event={event}
                      checked={events.includes(event)}
                      onChange={(e) => setEvents((all) => (e.target.checked ? [...all, event] : all.filter((x) => x !== event)))}
                    />
                    {event}
                  </label>
                ))}
              </div>
              {create.error ? <p data-webhook-refusal className="text-dense text-danger-text">{errorText(create.error)}</p> : null}
              <div>
                <Button intent="primary" size="small" data-webhook-create disabled={create.isPending} onClick={() => create.mutate()}>
                  {t('webhooks.create')}
                </Button>
              </div>
            </div>

            <SectionLabel>{t('webhooks.inbound')}</SectionLabel>
            <p className="text-meta text-ink-meta">{t('webhooks.inboundHelp')}</p>
            <ul className="flex flex-col">
              {data.data.sources.map((source) => (
                <li key={source.sourceId} data-inbound-source={source.name} className="flex flex-col gap-1 border-t border-t-rule py-2 first:border-t-0">
                  <span className="text-dense text-ink">
                    {source.name} → {source.flowName ?? t('webhooks.noAutomation')}
                    {source.active ? '' : ` · ${t('webhooks.off')}`}
                  </span>
                  {source.recent.map((event, index) => (
                    <span key={`${event.receivedAt}-${String(index)}`} className="text-meta text-ink-600">
                      {formatDateTime(event.receivedAt, format)} · {event.outcome}
                    </span>
                  ))}
                </li>
              ))}
            </ul>
            {token ? (
              <p data-inbound-token className="rounded-control bg-warning-tint px-3 py-2 text-dense text-ink">
                {t('webhooks.tokenOnce')} <code className="break-all">{`${apiBase}/public/hooks/${token}`}</code>
              </p>
            ) : null}
            {data.data.automations.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                <input data-inbound-name aria-label={t('webhooks.sourceName')} placeholder={t('webhooks.sourceName')} className={INPUT} value={sourceName} onChange={(e) => setSourceName(e.target.value)} />
                <select data-inbound-flow aria-label={t('webhooks.automation')} className={INPUT} value={flowId} onChange={(e) => setFlowId(e.target.value)}>
                  <option value="">{t('webhooks.chooseAutomation')}</option>
                  {data.data.automations.map((flow) => (
                    <option key={flow.flowId} value={flow.flowId}>{flow.name}</option>
                  ))}
                </select>
                <Button intent="secondary" size="small" data-inbound-create disabled={!flowId || createSource.isPending} onClick={() => createSource.mutate()}>
                  {t('webhooks.createSource')}
                </Button>
              </div>
            ) : null}
            {createSource.error ? <p className="text-dense text-danger-text">{errorText(createSource.error)}</p> : null}
            <p className="text-meta text-ink-meta">{t('webhooks.countNote', { values: { count: formatCount(data.data.endpoints.length, format) } })}</p>
          </>
        )}
      </div>
    </Panel>
  );
}

function EndpointRow({ endpoint }: { endpoint: Endpoint }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const deliveries = useQuery<WebhookDeliveriesResult, ApiError>({
    queryKey: ['webhook-deliveries', endpoint.endpointId],
    queryFn: () => api.webhookDeliveries({ path: { endpointId: endpoint.endpointId } }),
    enabled: open,
  });
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['webhook-deliveries', endpoint.endpointId] });
    await queryClient.invalidateQueries({ queryKey: ['webhooks'] });
  };
  const test = useMutation({ mutationFn: () => api.testWebhook({ path: { endpointId: endpoint.endpointId }, body: {} }), onSuccess: refresh });
  const replay = useMutation({ mutationFn: (deliveryId: string) => api.replayWebhookDelivery({ path: { deliveryId }, body: {} }), onSuccess: refresh });
  return (
    <div data-webhook={endpoint.name} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-dense text-ink">{endpoint.name}</span>
        <span className="break-all text-meta text-ink-meta">{endpoint.url}</span>
        <span className="text-meta text-ink-meta">
          {t('webhooks.recent', {
            values: {
              ok: formatCount(endpoint.recent.succeeded, format),
              failed: formatCount(endpoint.recent.failed + endpoint.recent.dead, format),
            },
          })}
        </span>
        <span className="ms-auto flex gap-2">
          <Button intent="secondary" size="small" data-webhook-test disabled={test.isPending} onClick={() => test.mutate()}>
            {t('webhooks.test')}
          </Button>
          <Button intent="ghost" size="small" data-webhook-log onClick={() => setOpen((value) => !value)}>
            {open ? t('webhooks.hideLog') : t('webhooks.log')}
          </Button>
        </span>
      </div>
      {test.data ? <p data-webhook-test-result className="text-dense text-ink">{t(`webhooks.status.${test.data.status}`)}</p> : null}
      {test.error ?? replay.error ? <p className="text-dense text-danger-text">{errorText((test.error ?? replay.error)!)}</p> : null}
      {open && deliveries.data ? (
        <ul data-webhook-deliveries className="flex flex-col gap-2">
          {deliveries.data.deliveries.map((delivery) => (
            <li key={delivery.deliveryId} data-delivery={delivery.status} className="flex flex-col gap-1 rounded-card bg-canvas p-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-dense text-ink">{delivery.event}</span>
                <span className="text-meta text-ink-meta">{t(`webhooks.status.${delivery.status}`)}</span>
                <span className="text-meta text-ink-meta">{formatDateTime(delivery.createdAt, format)}</span>
                {delivery.nextAttemptAt ? (
                  <span className="text-meta text-ink-meta">{t('webhooks.nextTry', { values: { at: formatDateTime(delivery.nextAttemptAt, format) } })}</span>
                ) : null}
                <Button intent="ghost" size="small" data-delivery-replay className="ms-auto" disabled={replay.isPending} onClick={() => replay.mutate(delivery.deliveryId)}>
                  {t('webhooks.replay')}
                </Button>
              </div>
              <details className="text-meta text-ink-600">
                <summary>{t('webhooks.payloadAndAttempts', { values: { count: String(delivery.attempts) } })}</summary>
                <pre className="overflow-x-auto whitespace-pre-wrap text-meta">{delivery.payload}</pre>
                <ul className="flex flex-col gap-1">
                  {delivery.attemptLog.map((attempt, index) => (
                    <li key={`${attempt.attemptedAt}-${String(index)}`} data-attempt={attempt.statusCode ?? 'none'}>
                      {formatDateTime(attempt.attemptedAt, format)} · {attempt.statusCode ?? '-'} · {attempt.durationMs ?? 0} ms
                      {attempt.error ? ` · ${attempt.error}` : ''}
                      {attempt.response ? ` · ${attempt.response.slice(0, 200)}` : ''}
                    </li>
                  ))}
                </ul>
              </details>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
